import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { X, CreditCard, Loader } from 'lucide-react';
import PropTypes from 'prop-types';
import { API_URLS } from '../constants/api';

// Check if authentication should be bypassed for development
const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

// Initialize Stripe only if auth is not bypassed and key is provided
const stripePromise = skipAuth
  ? Promise.resolve(null)
  : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY &&
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.trim() !== ''
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : Promise.resolve(null);

// Force reload to clear cache

const PaymentMethodForm = ({
  onSuccess,
  onCancel,
  isLoading,
  setIsLoading,
  subscriptionId
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || isLoading) {
      return;
    }

    setError(null);
    setIsLoading(true);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement
        });

      if (pmError) {
        setError(pmError.message);
        setIsLoading(false);
        return;
      }

      // Update payment method on backend
      const response = await fetch(API_URLS.UPDATE_PAYMENT_METHOD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          paymentMethodId: paymentMethod.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess('Payment method updated successfully!');
        // Loading state is cleared by onSuccess -> handleSuccess in parent
      } else {
        setError(data.error || 'Failed to update payment method');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error updating payment method:', err);
      setError('Failed to update payment method. Please try again.');
      setIsLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'system-ui, sans-serif',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#9e2146'
      }
    },
    hidePostalCode: false, // Show zip code field
    classes: {
      base: 'w-full'
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          New Payment Method
        </label>

        <div className="p-4 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
          <CardElement options={cardElementOptions} />
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Enter your card details including number, expiry, CVC, and zip code
        </p>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <CreditCard className="h-4 w-4" />
              Update Payment Method
            </>
          )}
        </button>
      </div>
    </form>
  );
};

PaymentMethodForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  subscriptionId: PropTypes.string.isRequired
};

const PaymentMethodUpdate = ({
  isOpen,
  onClose,
  onSuccess,
  subscriptionId
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = (message) => {
    setIsLoading(false);
    onSuccess(message);
    onClose();
  };

  const handleCancel = () => {
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Update Payment Method
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <CreditCard className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">
                  Secure Payment Update
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Your payment information is encrypted and secure. This will
                  update your default payment method for future billing.
                </p>
              </div>
            </div>
          </div>

          <Elements stripe={stripePromise}>
            <PaymentMethodForm
              onSuccess={handleSuccess}
              onCancel={handleCancel}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              subscriptionId={subscriptionId}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

PaymentMethodUpdate.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  subscriptionId: PropTypes.string.isRequired
};

export default PaymentMethodUpdate;
